import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community'
import { Badge } from 'antd'
import React from 'react'
import {
  AlignedRow,
  DragDropState,
  EntityData,
  MappingMutationParams,
} from '../types'

/**
 * Safely render a value, handling objects like location coordinates
 */
const safeRenderValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-'
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value)
  }
  if (typeof value === 'object') {
    // Handle location objects specifically
    if (value.lat !== undefined && value.lon !== undefined) {
      return `${Number(value.lat).toFixed(6)}, ${Number(value.lon).toFixed(6)}`
    }
    // For other objects, try to find a name/display field
    return (
      value.name || value.display_name || value.title || JSON.stringify(value)
    )
  }
  return String(value)
}

interface ColumnDefinitionsProps {
  leftTenant: string
  rightTenant: string
  isDragging: boolean
  draggedData: EntityData | null
  dragSource: 'left' | 'right' | null
  alignedRows: AlignedRow[]
  onDragStart: (data: EntityData, source: 'left' | 'right') => void
  onDragEnd: () => void
  onCreateMapping: (params: MappingMutationParams) => void
  entityType: string
  MappingSelectorComponent: React.ComponentType<{
    tenantName: string
    tenantEntityId: string
    side: 'left' | 'right'
    alignedRows: AlignedRow[]
    onSelect: (goid: string) => void
    entityType?: string
  }>
}

/**
 * Generate column definitions for the three-way mapping grid
 */
export const getColumnDefinitions = ({
  leftTenant,
  rightTenant,
  isDragging,
  draggedData,
  dragSource,
  alignedRows,
  onDragStart,
  onDragEnd,
  onCreateMapping,
  entityType,
  MappingSelectorComponent,
}: ColumnDefinitionsProps): (ColDef | ColGroupDef)[] => {
  const leftTenantLabel = leftTenant === 'none' ? 'Left Tenant' : leftTenant
  const rightTenantLabel = rightTenant === 'none' ? 'Right Tenant' : rightTenant

  return [
    // Left Tenant Group
    {
      headerName: leftTenantLabel,
      headerClass: 'tenant-group-header',
      children: [
        {
          headerName: 'Name',
          field: 'leftData.display_name',
          width: 200,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const data = params.data?.leftData
            if (!data)
              return React.createElement('div', {
                style: { height: '45px' },
              })
            return React.createElement(
              'div',
              {
                draggable: true,
                onDragStart: (e: React.DragEvent) => {
                  onDragStart(data, 'left')
                  e.dataTransfer.effectAllowed = 'link'
                },
                onDragEnd: onDragEnd,
                style: { cursor: 'grab', padding: '8px 0' },
              },
              safeRenderValue(data.display_name || data.name),
            )
          },
        },
        {
          headerName: 'Status',
          field: 'leftData.status',
          width: 100,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const isMapped = params.data?.isMapped && params.data?.leftData
            return isMapped
              ? React.createElement(Badge, {
                  status: 'success',
                  text: 'Mapped',
                })
              : params.data?.leftData
                ? React.createElement(Badge, {
                    status: 'default',
                    text: 'Unmapped',
                  })
                : null
          },
        },
        {
          headerName: 'Action',
          field: 'leftAction',
          width: 100,
          cellClass: 'left-tenant-last',
          headerClass: 'left-tenant-last',
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const hasMrd = !!params.data?.mrdData
            const hasLeftMapping = !!params.data?.leftData

            // Only show button when:
            // 1. There's NO MRD data (unmapped row)
            // 2. There IS a left tenant entity
            if (!hasMrd && hasLeftMapping && !params.data?.isMapped) {
              return React.createElement(MappingSelectorComponent, {
                tenantName: leftTenant,
                tenantEntityId: params.data.leftData?.source_id || '',
                side: 'left',
                alignedRows: alignedRows,
                entityType: entityType,
                onSelect: (goid: string) => {
                  onCreateMapping({
                    tenantName: leftTenant,
                    sourceId: params.data?.leftData?.source_id || '',
                    goid: goid,
                    displayName:
                      params.data?.leftData?.display_name ||
                      params.data?.leftData?.name ||
                      '',
                    mappingId:
                      params.data?.leftData?._id ||
                      params.data?.leftData?.id ||
                      params.data?.leftData?.source_id ||
                      '',
                  })
                },
              })
            }
            return null
          },
        },
      ],
    },

    // MRD Group
    {
      headerName: 'Master Reference Data',
      headerClass: 'mrd-group-header',
      children: [
        {
          headerName: 'Name',
          field: 'mrdData.name',
          width: 200,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const data = params.data?.mrdData
            if (!data)
              return React.createElement('div', {
                style: { height: '45px' },
              })
            return React.createElement(
              'div',
              {
                style: { padding: '8px 0' },
              },
              safeRenderValue(data.name || data.display_name),
            )
          },
        },
        {
          headerName: 'GOID',
          field: 'mrdData.goid',
          width: 120,
          cellClass: 'mrd-last',
          headerClass: 'mrd-last',
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const data = params.data?.mrdData
            if (!data)
              return React.createElement('div', {
                style: { height: '45px' },
              })
            return React.createElement(
              'div',
              {
                onDragOver: (e: React.DragEvent) => {
                  if (isDragging) {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'link'
                  }
                },
                onDrop: (e: React.DragEvent) => {
                  e.preventDefault()
                  if (draggedData && data.goid) {
                    const tenantName =
                      dragSource === 'left' ? leftTenant : rightTenant
                    onCreateMapping({
                      tenantName,
                      sourceId: draggedData.source_id || '',
                      goid: data.goid,
                      displayName:
                        draggedData.display_name || draggedData.name || '',
                      mappingId:
                        draggedData._id ||
                        draggedData.id ||
                        draggedData.source_id ||
                        '',
                    })
                  }
                },
                style: {
                  backgroundColor: isDragging
                    ? 'var(--theme-primary-dim)'
                    : 'transparent',
                  padding: '8px 0',
                  minHeight: '45px',
                  display: 'flex',
                  alignItems: 'center',
                },
              },
              data.goid,
            )
          },
        },
      ],
    },

    // Right Tenant Group
    {
      headerName: rightTenantLabel,
      headerClass: 'tenant-group-header',
      children: [
        {
          headerName: 'Action',
          field: 'rightAction',
          width: 100,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const hasMrd = !!params.data?.mrdData
            const hasRightMapping = !!params.data?.rightData

            // Only show button when:
            // 1. There's NO MRD data (unmapped row)
            // 2. There IS a right tenant entity
            if (!hasMrd && hasRightMapping && !params.data?.isMapped) {
              return React.createElement(MappingSelectorComponent, {
                tenantName: rightTenant,
                tenantEntityId: params.data.rightData?.source_id || '',
                side: 'right',
                alignedRows: alignedRows,
                entityType: entityType,
                onSelect: (goid: string) => {
                  onCreateMapping({
                    tenantName: rightTenant,
                    sourceId: params.data?.rightData?.source_id || '',
                    goid: goid,
                    displayName:
                      params.data?.rightData?.display_name ||
                      params.data?.rightData?.name ||
                      '',
                    mappingId:
                      params.data?.rightData?._id ||
                      params.data?.rightData?.id ||
                      params.data?.rightData?.source_id ||
                      '',
                  })
                },
              })
            }
            return null
          },
        },
        {
          headerName: 'Status',
          field: 'rightData.status',
          width: 100,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const isMapped = params.data?.isMapped && params.data?.rightData
            return isMapped
              ? React.createElement(Badge, {
                  status: 'success',
                  text: 'Mapped',
                })
              : params.data?.rightData
                ? React.createElement(Badge, {
                    status: 'default',
                    text: 'Unmapped',
                  })
                : null
          },
        },
        {
          headerName: 'Name',
          field: 'rightData.display_name',
          width: 200,
          cellRenderer: (params: ICellRendererParams<AlignedRow>) => {
            const data = params.data?.rightData
            if (!data)
              return React.createElement('div', {
                style: { height: '45px' },
              })
            return React.createElement(
              'div',
              {
                draggable: true,
                onDragStart: (e: React.DragEvent) => {
                  onDragStart(data, 'right')
                  e.dataTransfer.effectAllowed = 'link'
                },
                onDragEnd: onDragEnd,
                style: { cursor: 'grab', padding: '8px 0' },
              },
              safeRenderValue(data.display_name || data.name),
            )
          },
        },
      ],
    },
  ]
}

/**
 * CSS styles for column groups and borders
 */
export const columnGroupStyles = `
  .tenant-group-header {
    background-color: var(--bg-2) !important;
    font-weight: 600;
  }
  .mrd-group-header {
    background-color: var(--theme-primary-light) !important;
    font-weight: 600;
  }
  .ag-header-group-cell {
    border-right: 2px solid var(--border-color) !important;
  }
  /* Add borders between column groups */
  .left-tenant-last {
    border-right: 2px solid var(--border-color) !important;
  }
  .mrd-last {
    border-right: 2px solid var(--border-color) !important;
  }
  /* Also apply to header cells */
  .ag-header-cell.left-tenant-last {
    border-right: 2px solid var(--border-color) !important;
  }
  .ag-header-cell.mrd-last {
    border-right: 2px solid var(--border-color) !important;
  }
`
